import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { todayKey } from "@/domain/dates";
import type { Entry, Scenario, Subscription } from "@/domain/entities";
import { Button } from "../components/Button";
import { PencilIcon } from "../components/PencilIcon";
import { ScenarioPicker } from "../components/ScenarioPicker";
import { Screen } from "../components/Screen";
import { SwipeRow } from "../components/SwipeRow";
import { useApp } from "../container";
import { formatDate, formatEpisode } from "../format";
import { useQuery } from "../hooks/useQuery";
import { useLocale } from "../i18n/LocaleProvider";
import { format } from "../i18n/strings";
import styles from "./TodayScreen.module.css";

interface Row {
  subscription: Subscription;
  scenario: Scenario | undefined;
  entries: Entry[];
}

interface TodayData {
  rows: Row[];
  /** Any subscription at all, paused ones included: decides picker versus the settings note. */
  hasSubscriptions: boolean;
}

export function TodayScreen() {
  const app = useApp();
  const navigate = useNavigate();
  const { t, pick, locale } = useLocale();
  const today = todayKey(app.clock);
  const [pendingDelete, setPendingDelete] = useState<Entry | null>(null);
  const data = useQuery<TodayData>(async () => {
    const all = await app.store.subscriptions.listAll();
    const scenarios = await app.store.scenarios.list();
    const rows = await Promise.all(
      all.filter((s) => s.enabled).map(async (subscription) => ({
        subscription,
        scenario: scenarios.find((s) => s.id === subscription.scenarioId),
        entries: await app.store.entries.listByDate(subscription.id, today),
      })),
    );
    return { rows, hasSubscriptions: all.length > 0 };
  }, [today], app.changeSources);
  const rows = data.data?.rows;

  const skipDay = async ({ subscription, scenario }: Row) => {
    if (!scenario) return;
    const entry = await app.entries.getOrCreate(subscription, scenario, today);
    await app.entries.markSkipped(entry);
    void app.reminders.markDone(subscription.id);
  };

  const addEpisode = async ({ subscription, scenario }: Row) => {
    if (!scenario) return;
    const entry = await app.entries.createEpisode(subscription, scenario, today);
    navigate(`/answer/${subscription.id}/${today}/${entry.id}`);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    app.analytics.track("entry_deleted");
    await app.entries.remove(pendingDelete);
    setPendingDelete(null);
  };

  const deleteDialog = (
    <div className={styles.confirm} role="alertdialog" aria-label={t.questionnaire.deleteEntry}>
      <p>{t.questionnaire.deleteConfirm}</p>
      <div className={styles.actions}>
        <Button variant="ghost" onClick={() => setPendingDelete(null)}>{t.questionnaire.deleteNo}</Button>
        <Button variant="danger" onClick={confirmDelete}>{t.questionnaire.deleteYes}</Button>
      </div>
    </div>
  );

  /** Every scenario opens an entry the same way: by its id from the row, or a fresh one for the day. */
  const entryLink = (row: Row, entry?: Entry) =>
    entry ? `/answer/${row.subscription.id}/${today}/${entry.id}` : `/answer/${row.subscription.id}/${today}`;

  return (
    <Screen title={t.today.title}>
      <p className={styles.date}>{formatDate(today, locale)}</p>
      {data.data && !data.data.hasSubscriptions && (
        <>
          <ScenarioPicker />
          <Link to="/settings?transfer=1" className={styles.transferLink}>{t.transfer.fromBrowser}</Link>
        </>
      )}
      <ul className={styles.list}>
        {rows?.map((row) => {
          const episodic = row.scenario?.episodes !== undefined;
          const shown = episodic ? row.entries : row.entries.slice(0, 1);
          const recorded = shown.some((entry) => entry.status !== "partial");
          return (
            <li key={row.subscription.id} className={styles.card}>
              <span className={styles.name}>{row.scenario ? pick(row.scenario.name) : row.subscription.scenarioId}</span>
              {shown.length === 0 && <span className={styles.status}>{t.today.pending}</span>}
              {shown.map((entry, index) => {
                const summary = row.scenario ? formatEpisode(entry, row.scenario, locale) : "";
                if (pendingDelete?.id === entry.id) return <div key={entry.id}>{deleteDialog}</div>;
                return (
                  <SwipeRow key={entry.id} onDelete={() => setPendingDelete(entry)} deleteLabel={t.questionnaire.deleteEntry}>
                    <Link to={entryLink(row, entry)} className={styles.episodeLink}>
                      {entry.status === "skipped" ? (
                        <>
                          <span className={styles.status}>{t.today.skipped}</span>
                          <span className={styles.rowAction}>{t.today.undoSkip}</span>
                        </>
                      ) : (
                        <>
                          <span className={styles.entryNumber}>{format(t.today.entryNumber, { n: index + 1 })}</span>
                          {summary && <span>{summary}</span>}
                        </>
                      )}
                      <PencilIcon className={styles.editIcon} />
                    </Link>
                  </SwipeRow>
                );
              })}
              <div className={styles.actions}>
                {shown.length === 0 && (
                  <Link to={entryLink(row)}>
                    <Button variant="primary" tabIndex={-1}>{t.today.answer}</Button>
                  </Link>
                )}
                {episodic && shown.some((entry) => entry.status !== "skipped") && (
                  <Button variant="primary" onClick={() => addEpisode(row)}>{t.today.addEpisode}</Button>
                )}
                {!recorded && <Button variant="ghost" onClick={() => skipDay(row)}>{t.today.skipDay}</Button>}
              </div>
            </li>
          );
        })}
      </ul>
      {data.data?.hasSubscriptions && <p className={styles.note}>{t.today.moreInSettings}</p>}
    </Screen>
  );
}
